import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  studentService,
  batchService,
  STUDENT_TYPES,
  PROFESSIONAL_COURSES,
  SECONDARY_TRADES,
  buildCreatePayload,
  formatRwf,
  SECONDARY_TERM_FEE,
} from '../services';
import { getStudentTypeLabel, getProgramLabel, getBatchLabel, getStudentBatchId, formatBatchDates } from '../utils/studentDisplay';
import { validators } from '../utils/validators';
import {
  isBatchAtCapacity,
  getBatchSeatsLeft,
  getBatchStudentCount,
  getBatchCapacity,
} from '../utils/batchCapacity';

const srEase = [0.22, 1, 0.36, 1];
const categorySectionMotion = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.32, ease: srEase } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.22, ease: srEase } },
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const getInitialFormState = () => ({
  studentType: STUDENT_TYPES.SECONDARY,
  courses: [],
  batch: '',
  fullName: '', gender: '', dateOfBirth: '', phone: '', email: '',
  address: '', fieldOfStudy: '', schoolName: '', level: '',
  profession: '', employer: '', yearsOfExperience: '',
  emergencyContact: { name: '', phone: '', relation: '' },
});

const trackForStudentType = (studentType) =>
  studentType === STUDENT_TYPES.PROFESSIONAL ? 'professional' : 'secondary';

const SectionTitle = ({ label }) => (
  <h3 className="sr-section-title">{label}</h3>
);

const Field = ({ label, required, children }) => (
  <div className="sr-form-group">
    <label className="sr-label">{label}{required && <span className="sr-required">*</span>}</label>
    {children}
  </div>
);

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionMenuStudent, setActionMenuStudent] = useState(null);
  const [viewStudent, setViewStudent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formSaving, setFormSaving] = useState(false);
  const [formData, setFormData] = useState(getInitialFormState());
  const [batches, setBatches] = useState([]);
  const [batchStatsMap, setBatchStatsMap] = useState({});

  useEffect(() => { fetchStudents(); fetchBatches(); }, []);

  const fetchBatches = async () => {
    try {
      const data = await batchService.getAllBatches();
      const list = Array.isArray(data) ? data : [];
      setBatches(list);
      const entries = await Promise.all(
        list.map(async (b) => {
          try {
            const stats = await batchService.getBatchStats(b._id);
            return [b._id, stats];
          } catch {
            return [b._id, { total: 0 }];
          }
        })
      );
      setBatchStatsMap(Object.fromEntries(entries));
    } catch (err) {
      console.error('Failed to load batches:', err);
      setBatches([]);
      setBatchStatsMap({});
    }
  };

  useEffect(() => { setCurrentPage(1); }, [searchTerm, typeFilter, batchFilter]);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(''), 4000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const fetchStudents = async () => {
    try { setLoading(true); setError(''); const data = await studentService.getAllStudents(); setStudents(data); }
    catch (err) { setError(err.error || err.message || 'Failed to fetch students'); }
    finally { setLoading(false); }
  };

  const validateStudentForm = () => {
    let msg = validators.required(formData.fullName?.trim(), 'Full name');
    if (msg) return msg;
    msg = validators.required(formData.email?.trim(), 'Email address');
    if (msg) return msg;
    msg = validators.email(formData.email); if (msg) return msg;
    msg = validators.required(formData.phone?.trim(), 'Phone number');
    if (msg) return msg;
    msg = validators.phone(formData.phone); if (msg) return msg;
    msg = validators.dateNotInFuture(formData.dateOfBirth, 'Date of birth'); if (msg) return msg;
    if (formData.studentType === STUDENT_TYPES.PROFESSIONAL) {
      if (!formData.courses.length) return 'Select at least one training course';
    }
    msg = validators.required(formData.batch, 'Cohort / batch');
    if (msg) return msg;
    if (formData.emergencyContact?.phone) { msg = validators.phone(formData.emergencyContact.phone); if (msg) return msg; }
    return null;
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setError(''); setSuccessMessage('');
    const ve = validateStudentForm(); if (ve) { setError(ve); return; }
    setFormSaving(true);
    try {
      await studentService.createStudent(buildCreatePayload(formData));
      setSuccessMessage('Student created successfully!');
      resetForm();
      fetchStudents();
      fetchBatches();
    }
    catch (err) { setError(err.error || err.message || 'Failed to create student'); }
    finally { setFormSaving(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault(); setError(''); setSuccessMessage('');
    if (!selectedStudent) return;
    const ve = validateStudentForm(); if (ve) { setError(ve); return; }
    setFormSaving(true);
    try {
      await studentService.updateStudent(selectedStudent._id, buildCreatePayload(formData));
      setSuccessMessage('Student updated successfully!');
      resetForm();
      fetchStudents();
      fetchBatches();
    }
    catch (err) { setError(err.error || err.message || 'Failed to update student'); }
    finally { setFormSaving(false); }
  };

  const handleDelete = async (studentId) => {
    setError(''); setSuccessMessage('');
    try { await studentService.deleteStudent(studentId); setSuccessMessage('Student deleted.'); setDeleteConfirm(null); fetchStudents(); }
    catch (err) { setError(err.error || err.message || 'Failed to delete student'); setDeleteConfirm(null); }
  };

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setFormData({
      ...getInitialFormState(),
      studentType: student.studentType || STUDENT_TYPES.SECONDARY,
      courses: Array.isArray(student.courses)
        ? student.courses
        : student.course
          ? [student.course]
          : [],
      batch: getStudentBatchId(student) || '',
      fullName: student.fullName || '',
      gender: student.gender || '',
      dateOfBirth: student.dateOfBirth?.split?.('T')[0] || '',
      phone: student.phone || '',
      email: student.email || '',
      address: student.address || '',
      fieldOfStudy: student.fieldOfStudy || '',
      schoolName: student.schoolName || '',
      level: student.level || '',
      emergencyContact: {
        name: student.emergencyContact?.name || '',
        phone: student.emergencyContact?.phone || '',
        relation: student.emergencyContact?.relation || '',
      },
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData(getInitialFormState());
    setSelectedStudent(null);
    setShowForm(false);
    setFormSaving(false);
  };

  const handleSelectStudentType = (type) => {
    setFormData((prev) => {
      if (prev.studentType === type) return prev;
      return {
        ...getInitialFormState(),
        studentType: type,
        fullName: prev.fullName,
        gender: prev.gender,
        dateOfBirth: prev.dateOfBirth,
        phone: prev.phone,
        email: prev.email,
        address: prev.address,
        batch: '',
      };
    });
  };

  const cohortOptions = useMemo(() => {
    const track = trackForStudentType(formData.studentType);
    return [...batches]
      .filter((b) => b.track === track)
      .sort((a, b) => {
        const aRunning = a.status === 'running' ? 0 : 1;
        const bRunning = b.status === 'running' ? 0 : 1;
        if (aRunning !== bRunning) return aRunning - bRunning;
        return new Date(b.startDate || 0) - new Date(a.startDate || 0);
      });
  }, [batches, formData.studentType]);

  const selectedCohort = useMemo(
    () => batches.find((b) => String(b._id) === String(formData.batch)) || null,
    [batches, formData.batch]
  );

  const selectedCohortStats = selectedCohort ? batchStatsMap[selectedCohort._id] : null;
  const selectedCohortAtCapacity = selectedCohort
    ? isBatchAtCapacity(selectedCohort, selectedCohortStats)
    : false;

  const categoryOptions = [
    { type: STUDENT_TYPES.SECONDARY, icon: '🎓', title: 'Secondary', fee: `${formatRwf(SECONDARY_TERM_FEE)} / term` },
    { type: STUDENT_TYPES.PROFESSIONAL, icon: '💼', title: 'Professional', fee: 'From 250K RWF' },
  ];

  const closeFormModal = () => {
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('emergencyContact.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({ ...prev, emergencyContact: { ...prev.emergencyContact, [field]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCourseToggle = (course) => {
    setFormData((prev) => ({
      ...prev,
      courses: prev.courses.includes(course)
        ? prev.courses.filter((selected) => selected !== course)
        : [...prev.courses, course],
    }));
  };

  const sortedBatches = useMemo(() => {
    return [...batches].sort((a, b) => {
      const aRunning = a.status === 'running' ? 0 : 1;
      const bRunning = b.status === 'running' ? 0 : 1;
      if (aRunning !== bRunning) return aRunning - bRunning;
      return new Date(b.startDate || 0) - new Date(a.startDate || 0);
    });
  }, [batches]);

  const batchStudentCounts = useMemo(() => {
    const counts = { __none__: 0 };
    students.forEach((s) => {
      const id = getStudentBatchId(s);
      if (!id) counts.__none__ += 1;
      else counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  }, [students]);

  const selectedFilterBatch = useMemo(() => {
    if (!batchFilter || batchFilter === '__none__') return null;
    return sortedBatches.find((b) => String(b._id) === String(batchFilter)) || null;
  }, [batchFilter, sortedBatches]);

  const selectedFilterBatchName = useMemo(() => {
    if (!batchFilter) return '';
    if (batchFilter === '__none__') return 'No batch assigned';
    return selectedFilterBatch?.name || 'Selected batch';
  }, [batchFilter, selectedFilterBatch]);

  const matchesBatchFilter = (student) => {
    if (!batchFilter) return true;
    if (batchFilter === '__none__') return !getStudentBatchId(student);
    return getStudentBatchId(student) === batchFilter;
  };

  const filteredStudents = students.filter((s) => {
    if (typeFilter && s.studentType !== typeFilter) return false;
    if (!matchesBatchFilter(s)) return false;
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    return (
      s.fullName?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      s.fieldOfStudy?.toLowerCase().includes(q) ||
      (Array.isArray(s.courses) ? s.courses : s.course ? [s.course] : [])
        .some((course) => course.toLowerCase().includes(q)) ||
      s.schoolName?.toLowerCase().includes(q) ||
      getBatchLabel(s).toLowerCase().includes(q) ||
      s.phone?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedStudents = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredStudents.slice(start, start + pageSize);
  }, [filteredStudents, safePage, pageSize]);

  const rangeStart = filteredStudents.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, filteredStudents.length);

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
  const avatarColors = ['#1e40af', '#7c3aed', '#dc2626', '#059669', '#ea580c', '#0891b2', '#c2410c', '#0d9488'];
  const getAvatarColor = (name) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];

  const formatStudentDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const openEditFromMenu = (student) => {
    setActionMenuStudent(null);
    handleEdit(student);
  };

  const openDeleteFromMenu = (student) => {
    setActionMenuStudent(null);
    setDeleteConfirm(student);
  };

  const levelColor = (level) => {
    if (!level) return 'db-badge-muted';
    if (level.includes('3')) return 'db-badge-blue';
    if (level.includes('4')) return 'db-badge-purple';
    if (level.includes('5')) return 'db-badge-gold';
    return 'db-badge-green';
  };

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .sm-root {
          background: var(--bg);
          color: var(--text);
          font-family: var(--font-body), system-ui, -apple-system, 'Inter', sans-serif;
          min-height: 100vh;
          padding: 24px;
          line-height: 1.6;
        }

        /* ── TOAST ── */
        .sm-toast {
          position: fixed; top: 20px; right: 20px; z-index: 1000;
          display: flex; align-items: center; gap: 12px;
          padding: 16px 20px;
          border-radius: 6px;
          font-size: 14px; font-weight: 500;
          box-shadow: var(--shadow-lg);
          animation: slideIn 0.2s ease;
          max-width: 400px;
          border-left: 4px solid;
        }
        .sm-toast-success { 
          background: #f0fdf4; 
          border-left-color: var(--success-green); 
          color: var(--success-green); 
          border: 1px solid #d1fae5;
        }
        .sm-toast-error { 
          background: #fef2f2; 
          border-left-color: var(--error-red); 
          color: var(--error-red); 
          border: 1px solid #fecaca;
        }
        @keyframes slideIn { from { opacity:0; transform: translateX(100%); } to { opacity:1; transform: translateX(0); } }

        /* ── PAGE HEADER ── */
        .sm-page-header {
          background: var(--background-white);
          border-radius: 8px;
          padding: 24px;
          margin-bottom: 24px;
          border: 1px solid var(--border-light);
          box-shadow: var(--shadow-sm);
        }
        .sm-page-eyebrow {
          display: inline-block;
          background: var(--primary-blue);
          color: white;
          border-radius: 12px; 
          padding: 6px 16px;
          font-size: 12px; 
          font-weight: 600; 
          text-transform: uppercase;
          letter-spacing: 0.025em;
          margin-bottom: 12px;
        }
        .sm-page-title {
          font-size: 28px; 
          font-weight: 700; 
          letter-spacing: -0.025em; 
          line-height: 1.2;
          color: var(--text-dark);
          margin-bottom: 8px;
        }
        .sm-page-title span {
          color: var(--primary-blue);
        }
        .sm-page-sub { 
          font-size: 16px; 
          color: var(--text-medium); 
          line-height: 1.5;
        }

        .sm-header-stats { 
          display: flex; 
          gap: 16px; 
          flex-wrap: wrap; 
          margin-top: 16px;
        }
        .sm-mini-stat {
          background: var(--background-white); 
          border: 1px solid var(--border-light);
          border-radius: 6px; 
          padding: 16px 20px;
          text-align: center; 
          min-width: 120px;
          box-shadow: var(--shadow-sm);
        }
        .sm-mini-stat-num {
          font-size: 24px; 
          font-weight: 700;
          color: var(--text-dark);
          line-height: 1;
        }
        .sm-mini-stat-label { 
          font-size: 12px; 
          color: var(--text-light); 
          font-weight: 500; 
          text-transform: uppercase; 
          letter-spacing: 0.025em; 
          margin-top: 4px; 
        }

        /* ── TOOLBAR ── */
        .sm-toolbar {
          display: flex; align-items: center; gap: 12px;
          margin-bottom: 24px; flex-wrap: wrap;
        }
        .sm-search {
          flex: 1; min-width: 200px; max-width: 400px;
          display: flex; align-items: center; gap: 10px;
          background: var(--surface); border: 1px solid var(--border);
          border-radius: var(--radius-sm); padding: 11px 16px;
          transition: border-color 0.2s;
        }
        .sm-search:focus-within { border-color: var(--border-hi); }
        .sm-search-icon { font-size: 15px; color: var(--muted); flex-shrink: 0; }
        .sm-search input {
          flex: 1; border: none; outline: none; background: none;
          font-size: 13px; color: var(--text); font-family: var(--font-body);
        }
        .sm-search input::placeholder { color: var(--muted); }

        .sm-btn-add {
          display: flex; align-items: center; gap: 8px;
          padding: 12px 20px;
          background: var(--primary-blue);
          color: white; border: none; border-radius: 6px;
          font-size: 14px; font-weight: 600; cursor: pointer;
          font-family: inherit;
          transition: background-color 0.2s, transform 0.1s; white-space: nowrap;
        }
        .sm-btn-add:hover { background: #1e3a8a; transform: translateY(-1px); }
        .sm-btn-add.cancel {
          background: var(--error-red); color: white;
          border: none;
        }
        .sm-btn-add.cancel:hover { background: #b91c1c; }

        .sm-btn-refresh {
          display: flex; align-items: center; gap: 8px;
          padding: 12px 16px;
          background: var(--background-white); border: 1px solid var(--border-medium);
          border-radius: 6px; color: var(--text-medium);
          font-size: 14px; font-weight: 500; cursor: pointer;
          font-family: inherit; transition: all 0.2s; white-space: nowrap;
        }
        .sm-btn-refresh:hover { border-color: var(--primary-blue); color: var(--primary-blue); }

        .sm-batch-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 16px;
          padding: 16px 18px;
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
        }
        .sm-batch-bar-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-light);
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 6px;
        }
        .sm-batch-select {
          min-width: 260px;
          max-width: 100%;
        }
        .sm-batch-summary {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 20px;
          padding: 14px 18px;
          background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
          border: 1px solid #bfdbfe;
          border-radius: 8px;
        }
        .sm-batch-summary-title {
          font-size: 15px;
          font-weight: 700;
          color: var(--text-dark);
        }
        .sm-batch-summary-count {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 999px;
          background: var(--surface);
          border: 1px solid #93c5fd;
          color: #1d4ed8;
          font-size: 13px;
          font-weight: 700;
        }
        .sm-batch-summary-meta {
          font-size: 12px;
          color: var(--text-medium);
        }
        .sm-batch-summary-clear {
          margin-left: auto;
          padding: 7px 12px;
          border: 1px solid var(--border-medium);
          border-radius: 6px;
          background: var(--surface);
          color: var(--text-medium);
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
        }
        .sm-batch-summary-clear:hover {
          border-color: var(--primary-blue);
          color: var(--primary-blue);
        }

        /* ── FORM MODAL ── */
        .sm-form-overlay {
          position: fixed;
          inset: 0;
          z-index: 210;
          background: var(--overlay);
          backdrop-filter: blur(3px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.2s ease;
        }

        .sm-form-panel {
          width: min(980px, 100%);
          max-height: min(92vh, 900px);
          display: flex;
          flex-direction: column;
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 14px;
          box-shadow: 0 24px 60px rgba(2, 6, 23, 0.28);
          overflow: hidden;
          animation: modalUp 0.22s ease;
        }

        .sm-form-panel form {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          overflow: hidden;
        }

        @keyframes modalUp {
          from { opacity: 0; transform: translateY(8px) scale(0.99); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .sm-form-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 22px 24px 18px;
          border-bottom: 1px solid var(--border-light);
          background: var(--surface);
        }

        .sm-form-title {
          display: flex;
          align-items: center;
          gap: 12px;
          color: var(--text-dark);
          font-size: 20px;
          font-weight: 700;
        }

        .sm-form-title-wrap {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .sm-form-eyebrow {
          display: inline-block;
          width: fit-content;
          background: var(--primary-blue);
          color: #ffffff;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          padding: 4px 12px;
          border-radius: 999px;
        }

        .sm-form-subtitle {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-medium);
        }

        .sm-form-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          background: #dbeafe;
          color: var(--primary-blue);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          border: 1px solid #bfdbfe;
        }

        .sm-btn-close {
          margin-left: auto;
          padding: 9px 12px;
          border: 1px solid var(--border-medium);
          border-radius: 8px;
          background: var(--surface);
          color: var(--text-medium);
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .sm-btn-close:hover {
          background: var(--bg);
          border-color: #9ca3af;
        }

        /* Step indicator */
        .sm-steps-bar {
          border-bottom: 1px solid var(--border-light);
          background: var(--bg);
          padding: 10px 24px;
        }

        .sm-steps {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sm-step {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-light);
          cursor: pointer;
          transition: color 0.2s;
        }

        .sm-step.active { color: var(--primary-blue); }
        .sm-step.done { color: var(--success-green); }

        .sm-step-num {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--surface);
          border: 1px solid var(--border-medium);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          transition: all 0.2s;
        }

        .sm-step.active .sm-step-num {
          background: #dbeafe;
          border-color: #93c5fd;
          color: var(--primary-blue);
        }

        .sm-step.done .sm-step-num {
          background: #dcfce7;
          border-color: #86efac;
          color: var(--success-green);
        }

        .sm-step-divider {
          width: 20px;
          height: 1px;
          background: var(--border-medium);
        }

        .sm-form-body {
          flex: 1;
          min-height: 0;
          padding: 24px;
          overflow-y: auto;
        }

        /* Registration-aligned form (same as StudentRegistration) */
        .sm-form-panel .sr-section-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-dark);
          margin: 28px 0 16px;
          padding-bottom: 8px;
          border-bottom: 2px solid var(--primary-blue);
          position: relative;
        }
        .sm-form-panel .sr-section-title:first-of-type { margin-top: 0; }
        .sm-form-panel .sr-section-title::before {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 40px;
          height: 2px;
          background: var(--accent-orange);
        }
        .sm-form-panel .sr-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .sm-form-panel .sr-form-row--3 {
          grid-template-columns: 1fr 1fr 1fr;
          gap: 12px;
        }
        .sm-form-panel .sr-form-row .sr-form-group { margin-bottom: 0; }
        .sm-form-panel .sr-form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 16px;
        }
        .sm-form-panel .sr-form-group:last-child { margin-bottom: 0; }
        .sm-form-panel .sr-label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-dark);
        }
        .sm-form-panel .sr-required { color: var(--error-red); margin-left: 4px; }
        .sm-form-panel .sr-input,
        .sm-form-panel .sr-select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid var(--border-medium);
          border-radius: 8px;
          font-family: inherit;
          font-size: 15px;
          color: var(--text-dark);
          background: var(--surface);
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .sm-form-panel .sr-input:focus,
        .sm-form-panel .sr-select:focus {
          border-color: var(--primary-blue);
          box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
        }
        .sm-form-panel .sr-input:disabled,
        .sm-form-panel .sr-select:disabled {
          background: var(--background-gray);
          color: var(--text-light);
          cursor: not-allowed;
        }
        .sm-form-panel .sr-select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' fill='none' viewBox='0 0 24 24'%3E%3Cpath stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 16px center;
          padding-right: 48px;
          cursor: pointer;
        }
        .sm-form-panel .sr-segment {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4px;
          padding: 4px;
          background: var(--background-gray);
          border-radius: 10px;
          margin-bottom: 8px;
        }
        .sm-form-panel .sr-segment-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 108px;
          padding: 20px 16px;
          border: 2px solid transparent;
          border-radius: 8px;
          background: transparent;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.2s;
        }
        .sm-form-panel .sr-segment-btn.active {
          background: var(--surface);
          border-color: var(--primary-blue);
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }
        .sm-form-panel .sr-segment-btn:not(.active):hover { background: rgba(255, 255, 255, 0.5); }
        .sm-form-panel .sr-segment-content { text-align: center; }
        .sm-form-panel .sr-segment-icon { font-size: 32px; line-height: 1; margin-bottom: 8px; display: block; }
        .sm-form-panel .sr-segment-title { font-size: 16px; font-weight: 600; color: var(--text-dark); display: block; }
        .sm-form-panel .sr-segment-btn:not(.active) .sr-segment-title { color: var(--text-medium); }
        .sm-form-panel .sr-segment-fee { font-size: 13px; font-weight: 600; color: var(--accent-orange); display: block; margin-top: 4px; }
        .sm-form-panel .sr-segment-btn:not(.active) .sr-segment-fee { color: var(--text-light); }
        .sm-form-panel .sr-category-fields { margin-top: 4px; }
        .sm-cohort-hint {
          font-size: 13px;
          color: #b45309;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 8px;
          padding: 10px 12px;
          margin: -4px 0 16px;
          line-height: 1.45;
        }
        .sm-cohort-capacity {
          margin: -4px 0 18px;
          padding: 12px 14px;
          border-radius: 10px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
        }
        .sm-cohort-capacity.over {
          background: #fffbeb;
          border-color: #fde68a;
        }
        .sm-cohort-capacity-title {
          font-size: 14px;
          font-weight: 700;
          color: #065f46;
          margin-bottom: 4px;
        }
        .sm-cohort-capacity.over .sm-cohort-capacity-title { color: #92400e; }
        .sm-cohort-capacity-meta {
          font-size: 12px;
          color: var(--muted);
        }
        .sm-cohort-capacity-warn {
          margin-top: 8px;
          font-size: 12px;
          line-height: 1.45;
          color: #92400e;
          font-weight: 600;
        }
        .sm-form-panel .sr-details {
          border: 1px solid var(--border-medium);
          border-radius: 8px;
          background: var(--surface);
          margin-top: 8px;
        }
        .sm-form-panel .sr-details summary {
          list-style: none;
          cursor: pointer;
          padding: 16px 20px;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-dark);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .sm-form-panel .sr-details summary::-webkit-details-marker { display: none; }
        .sm-form-panel .sr-details summary::after { content: '+ Show'; font-size: 13px; font-weight: 500; color: var(--text-light); }
        .sm-form-panel .sr-details[open] summary::after { content: '− Hide'; }
        .sm-form-panel .sr-details-body { padding: 20px; }
        .sm-form-footer {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid var(--border-light);
          background: var(--surface);
          box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.06);
        }

        .sm-btn-save {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-width: 160px;
          padding: 12px 24px;
          background: var(--primary-blue);
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-family: inherit;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .sm-btn-save:hover:not(:disabled) { background: var(--primary-dark); }
        .sm-btn-save:disabled {
          background: var(--text-light);
          cursor: not-allowed;
        }

        /* Fields */
        .sm-field-grid { display: grid; gap: 16px; }
        .sm-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .sm-field-row-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }

        .sm-field { display: flex; flex-direction: column; gap: 7px; }

        .sm-label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-dark);
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .sm-label-req { color: var(--error-red); font-size: 14px; line-height: 1; }

        .sm-input, .sm-select {
          background: var(--surface);
          border: 2px solid var(--border-medium);
          border-radius: 8px;
          padding: 12px 16px;
          font-size: 15px;
          color: var(--text-dark);
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          width: 100%;
        }

        .sm-input:focus, .sm-select:focus {
          border-color: var(--primary-blue);
          box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.12);
        }

        .sm-input::placeholder { color: var(--text-light); }
        .sm-select {
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 14px center;
          padding-right: 36px;
        }

        .sm-select option { color: var(--text-dark); }

        .sm-section-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 2px 0 6px;
        }

        .sm-section-label {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-dark);
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .sm-section-line { flex: 1; height: 1px; background: var(--border-light); }

        .sm-form-footer-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
        }

        .sm-btn-submit {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: var(--primary-blue);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .sm-btn-submit:hover { background: #1e3a8a; }

        .sm-btn-cancel {
          padding: 12px 16px;
          background: var(--surface);
          border: 1px solid var(--border-medium);
          border-radius: 8px;
          color: var(--text-medium);
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .sm-btn-cancel:hover { background: var(--bg); border-color: #9ca3af; }

        .sm-step-nav { display: flex; gap: 8px; }

        .sm-btn-step {
          padding: 11px 14px;
          background: var(--surface);
          border: 1px solid var(--border-medium);
          border-radius: 8px;
          color: var(--text-medium);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .sm-btn-step:hover { border-color: #9ca3af; background: var(--bg); }

        .sm-btn-step.next {
          background: var(--link-hover-bg);
          border-color: #bfdbfe;
          color: var(--primary-blue);
        }

        /* ── TABLE ── */
        .sm-table-card {
          background: var(--surface); border: 1px solid var(--border);
          border-radius: 12px; overflow: hidden;
        }
        .sm-table-top {
          display: flex; align-items: center; justify-content: space-between;
          padding: 18px 24px; border-bottom: 1px solid var(--border);
          flex-wrap: wrap; gap: 12px;
          background: linear-gradient(135deg, rgba(96,165,250,0.04), transparent);
        }
        .sm-table-title {
          font-family: var(--font-head); font-size: 16px; font-weight: 700;
          display: flex; align-items: center; gap: 8px;
        }
        .sm-table-count {
          background: rgba(96,165,250,0.12); border: 1px solid rgba(96,165,250,0.2);
          color: var(--accent); border-radius: 12px;
          padding: 2px 10px; font-size: 12px; font-weight: 700;
        }

        .sm-scroll { overflow-x: auto; }
        .sm-scroll::-webkit-scrollbar { height: 4px; }
        .sm-scroll::-webkit-scrollbar-thumb { background: var(--border-light); border-radius: 6px; }

        .sm-table { width: 100%; border-collapse: collapse; min-width: 700px; }
        .sm-table th {
          padding: 13px 20px; font-size: 10px; font-weight: 800;
          color: var(--muted); text-transform: uppercase; letter-spacing: 1px;
          border-bottom: 1px solid var(--border); text-align: left;
          background: var(--border-light); white-space: nowrap;
        }
        .sm-table td {
          padding: 14px 20px; font-size: 13px; color: var(--text);
          border-bottom: 1px solid var(--border-light);
          vertical-align: middle;
        }
        .sm-table tbody tr { transition: background 0.15s; }
        .sm-table tbody tr:hover { background: rgba(96,165,250,0.04); }
        .sm-table tbody tr:last-child td { border-bottom: none; }

        .sm-student-cell { display: flex; align-items: center; gap: 10px; }
        .sm-ava {
          width: 36px; height: 36px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 800; color: white;
          font-family: var(--font-head); flex-shrink: 0;
          letter-spacing: -0.5px;
        }
        .sm-student-name { font-weight: 600; font-size: 13px; }
        .sm-student-email { font-size: 11px; color: var(--muted); margin-top: 1px; }

        .db-badge {
          display: inline-flex; align-items: center;
          padding: 4px 10px; border-radius: 12px;
          font-size: 11px; font-weight: 600; letter-spacing: 0.2px; white-space: nowrap;
        }
        .db-badge-blue { background: rgba(96,165,250,0.12); color: #93C5FD; border: 1px solid rgba(96,165,250,0.2); }
        .db-badge-purple { background: rgba(167,139,250,0.12); color: #C4B5FD; border: 1px solid rgba(167,139,250,0.2); }
        .db-badge-gold { background: rgba(251,191,36,0.12); color: #FCD34D; border: 1px solid rgba(251,191,36,0.2); }
        .db-badge-green { background: rgba(52,211,153,0.12); color: #6EE7B7; border: 1px solid rgba(52,211,153,0.2); }
        .db-badge-muted { background: var(--border-light); color: var(--muted); border: 1px solid var(--border); }

        .sm-btn-edit, .sm-btn-del, .sm-btn-pay {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 7px 12px; border-radius: var(--radius-xs);
          font-size: 12px; font-weight: 600; cursor: pointer;
          font-family: var(--font-body); transition: all 0.2s; border: 1px solid;
          white-space: nowrap;
        }
        .sm-btn-edit {
          background: rgba(96,165,250,0.08); border-color: rgba(96,165,250,0.2);
          color: var(--accent);
        }
        .sm-btn-edit:hover { background: rgba(96,165,250,0.15); border-color: rgba(96,165,250,0.4); }
        .sm-btn-del {
          background: rgba(248,113,113,0.08); border-color: rgba(248,113,113,0.2);
          color: var(--error-red);
        }
        .sm-btn-del:hover { background: rgba(248,113,113,0.15); border-color: rgba(248,113,113,0.4); }
        .sm-btn-pay {
          background: rgba(16,185,129,0.08); border-color: rgba(16,185,129,0.25); color: #047857;
        }
        .sm-btn-pay:hover { background: rgba(16,185,129,0.15); border-color: rgba(16,185,129,0.4); }
        .sm-actions-cell { display: flex; gap: 6px; align-items: center; justify-content: flex-end; }
        .sm-btn-more {
          width: 36px; height: 36px; border-radius: 8px;
          border: 1px solid var(--border-medium); background: var(--surface);
          color: var(--text-medium); font-size: 18px; line-height: 1;
          cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
        }
        .sm-btn-more:hover { background: var(--surface2); border-color: var(--primary-blue); color: var(--text-dark); }

        .sm-actions-menu-modal {
          width: min(380px, calc(100vw - 32px));
          background: var(--surface); border-radius: 18px;
          border: 1px solid var(--border);
          box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.18);
          overflow: hidden; animation: modalIn 0.22s cubic-bezier(0.34, 1.2, 0.64, 1);
        }
        .sm-actions-menu-head {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 14px;
          padding: 20px 20px 16px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          border-bottom: 1px solid #f1f5f9;
        }
        .sm-actions-menu-head-main { display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1; }
        .sm-actions-menu-av {
          width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 800; color: #fff;
        }
        .sm-actions-menu-eyebrow {
          font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #64748b; margin-bottom: 4px;
        }
        .sm-actions-menu-title {
          font-size: 16px; font-weight: 700; color: var(--text);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .sm-actions-menu-sub { font-size: 12px; color: #64748b; margin-top: 4px; }
        .sm-actions-menu-close {
          width: 34px; height: 34px; border: 1px solid #e2e8f0; border-radius: 10px;
          background: var(--surface); color: #64748b; cursor: pointer; font-size: 15px;
          display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .sm-actions-menu-close:hover { background: var(--bg); color: var(--text); }
        .sm-actions-menu-body { padding: 14px 16px 16px; }
        .sm-actions-menu-label {
          font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: var(--muted); margin-bottom: 10px; padding-left: 2px;
        }
        .sm-actions-menu-list { display: flex; flex-direction: column; gap: 6px; }
        .sm-actions-menu-item {
          display: flex; align-items: center; gap: 12px; width: 100%;
          padding: 12px; border: 1px solid #f1f5f9; border-radius: 12px;
          background: var(--surface); font-family: inherit; cursor: pointer; text-align: left;
          transition: border-color 0.15s, background 0.15s, box-shadow 0.15s, transform 0.15s;
        }
        .sm-actions-menu-item:hover {
          background: var(--bg); border-color: #e2e8f0;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05); transform: translateY(-1px);
        }
        .sm-actions-menu-item-icon {
          width: 40px; height: 40px; border-radius: 10px;
          display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .sm-actions-menu-item-icon svg { width: 18px; height: 18px; }
        .sm-actions-menu-item-icon.view { background: var(--link-hover-bg); color: #2563eb; border: 1px solid #dbeafe; }
        .sm-actions-menu-item-icon.edit { background: #f5f3ff; color: #7c3aed; border: 1px solid #ddd6fe; }
        .sm-actions-menu-item-icon.delete { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .sm-actions-menu-item-text { flex: 1; min-width: 0; }
        .sm-actions-menu-item-title { display: block; font-size: 14px; font-weight: 600; color: var(--text); }
        .sm-actions-menu-item-desc { display: block; font-size: 12px; color: #64748b; margin-top: 2px; }
        .sm-actions-menu-item-chevron { color: #cbd5e1; font-size: 18px; flex-shrink: 0; }
        .sm-actions-menu-divider { height: 1px; background: var(--bg); margin: 12px 0 10px; }
        .sm-actions-menu-item.danger { border-color: #fee2e2; background: #fffbfb; }
        .sm-actions-menu-item.danger .sm-actions-menu-item-title { color: #b91c1c; }
        .sm-actions-menu-item.danger:hover { background: #fef2f2; border-color: #fecaca; }

        .sm-view-modal {
          background: var(--surface); border-radius: 18px; border: 1px solid var(--border);
          max-width: 620px; width: 100%; max-height: 90vh;
          display: flex; flex-direction: column; overflow: hidden;
          box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.18);
          animation: modalIn 0.22s cubic-bezier(0.34, 1.2, 0.64, 1);
        }
        .sm-view-head {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 14px;
          padding: 20px 24px; border-bottom: 1px solid #f1f5f9;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
        }
        .sm-view-head-left { display: flex; align-items: center; gap: 14px; }
        .sm-view-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: var(--link-hover-bg); border: 1px solid #dbeafe; color: #2563eb;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .sm-view-icon svg { width: 20px; height: 20px; }
        .sm-view-title { font-size: 18px; font-weight: 700; color: var(--text); }
        .sm-view-sub { font-size: 13px; color: #64748b; margin-top: 3px; }
        .sm-view-body { padding: 20px 24px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 16px; }
        .sm-view-student-card {
          display: flex; align-items: center; gap: 14px; padding: 16px;
          border-radius: 14px; background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
          border: 1px solid #e2e8f0;
        }
        .sm-view-student-av {
          width: 52px; height: 52px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; font-weight: 800; color: #fff;
        }
        .sm-view-student-name { font-size: 17px; font-weight: 700; color: var(--text); }
        .sm-view-student-meta { font-size: 12px; color: #64748b; margin-top: 4px; }
        .sm-view-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .sm-view-field {
          padding: 14px; border-radius: 12px; background: var(--surface); border: 1px solid #e2e8f0;
        }
        .sm-view-field.full { grid-column: 1 / -1; }
        .sm-view-field-label {
          font-size: 10px; font-weight: 700; color: var(--muted);
          text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px;
        }
        .sm-view-field-val { font-size: 13px; font-weight: 600; color: var(--text); line-height: 1.45; word-break: break-word; }
        .sm-view-foot {
          padding: 16px 24px; border-top: 1px solid #f1f5f9;
          display: flex; gap: 10px; justify-content: flex-end; background: var(--bg);
        }
        .sm-btn-view-edit {
          padding: 11px 18px; border-radius: 8px; border: 1px solid #ddd6fe;
          background: #f5f3ff; color: #6d28d9; font-size: 14px; font-weight: 600;
          cursor: pointer; font-family: inherit;
        }
        .sm-btn-view-edit:hover { background: #ede9fe; }

        .sm-pagination {
          display: flex; align-items: center; justify-content: space-between; gap: 16px;
          flex-wrap: wrap; padding: 14px 20px;
          border-top: 1px solid var(--border-light); background: var(--bg);
        }
        .sm-pagination-info { font-size: 13px; color: var(--muted); }
        .sm-pagination-info strong { color: var(--text); font-weight: 700; }
        .sm-pagination-controls {
          display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
        }
        .sm-pagination-size {
          padding: 8px 12px; border-radius: 8px; border: 1px solid var(--border-medium);
          background: var(--surface); font-size: 13px; font-family: inherit; color: var(--text);
          cursor: pointer; margin-right: 4px;
        }
        .sm-pagination-btn {
          padding: 8px 14px; border-radius: 8px; border: 1px solid var(--border-medium);
          background: var(--surface); font-size: 13px; font-weight: 600; font-family: inherit;
          color: var(--text-medium); cursor: pointer; transition: all 0.15s;
        }
        .sm-pagination-btn:hover:not(:disabled) {
          border-color: var(--primary-blue); color: var(--primary-blue); background: var(--link-hover-bg);
        }
        .sm-pagination-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .sm-pagination-page {
          font-size: 13px; font-weight: 600; color: var(--text-medium);
          padding: 0 8px; min-width: 100px; text-align: center;
        }
        .sm-student-program { font-size: 11px; color: var(--secondary-blue); margin-top: 2px; font-weight: 600; }

        /* ── MOBILE CARDS ── */
        .sm-cards { display: none; flex-direction: column; gap: 12px; }
        .sm-card {
          background: var(--surface2); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 18px;
          transition: border-color 0.2s;
          animation: fadeUp 0.3s ease both;
        }
        .sm-card:hover { border-color: var(--border-light); }
        @keyframes fadeUp { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform: translateY(0); } }
        .sm-card-head {
          display: flex; align-items: center; gap: 12px; margin-bottom: 14px;
        }
        .sm-card-ava {
          width: 46px; height: 46px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; font-weight: 800; color: white;
          font-family: var(--font-head); flex-shrink: 0;
        }
        .sm-card-name { font-family: var(--font-head); font-size: 15px; font-weight: 700; margin-bottom: 3px; }
        .sm-card-sub { font-size: 12px; color: var(--muted); }
        .sm-card-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
        .sm-card-field dt { font-size: 10px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; }
        .sm-card-field dd { font-size: 12px; color: var(--text); word-break: break-all; }
        .sm-card-actions { display: flex; gap: 8px; padding-top: 14px; border-top: 1px solid var(--border); }
        .sm-card-actions .sm-btn-edit, .sm-card-actions .sm-btn-del, .sm-card-actions .sm-btn-pay { flex: 1; justify-content: center; }

        /* ── EMPTY / LOADING ── */
        .sm-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 64px 24px; gap: 12px;
        }
        .sm-empty-icon { font-size: 48px; opacity: 0.3; }
        .sm-empty-title { font-family: var(--font-head); font-size: 18px; font-weight: 700; color: var(--muted); }
        .sm-empty-sub { font-size: 13px; color: var(--muted); text-align: center; }

        .sm-loading {
          display: flex; align-items: center; justify-content: center;
          padding: 64px; gap: 8px;
        }
        .sm-dot {
          width: 8px; height: 8px; border-radius: 50%;
          animation: bounce 1.2s ease-in-out infinite;
        }
        .sm-dot:nth-child(1) { background: var(--accent); }
        .sm-dot:nth-child(2) { background: var(--secondary-blue); animation-delay: 0.2s; }
        .sm-dot:nth-child(3) { background: var(--error-red); animation-delay: 0.4s; }
        @keyframes bounce { 0%,80%,100% { transform:scale(0.6); opacity:0.4; } 40% { transform:scale(1); opacity:1; } }

        /* ── DELETE MODAL ── */
        .sm-modal-overlay {
          position: fixed; inset: 0; z-index: 200;
          background: var(--overlay); backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center; padding: 20px;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
        .sm-modal {
          background: var(--surface); border: 1px solid rgba(248,113,113,0.2);
          border-radius: 12px; padding: 32px; max-width: 420px; width: 100%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.6);
          animation: modalIn 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes modalIn { from { opacity:0; transform:scale(0.92); } to { opacity:1; transform:scale(1); } }
        .sm-modal-icon {
          width: 52px; height: 52px; border-radius: 12px;
          background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.25);
          display: flex; align-items: center; justify-content: center;
          font-size: 24px; margin-bottom: 18px;
        }
        .sm-modal-title { font-family: var(--font-head); font-size: 18px; font-weight: 700; margin-bottom: 8px; }
        .sm-modal-sub { font-size: 13px; color: var(--muted); margin-bottom: 24px; line-height: 1.6; }
        .sm-modal-name {
          display: inline-block; padding: 4px 10px;
          background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.2);
          border-radius: 6px; color: #FCA5A5; font-weight: 600;
        }
        .sm-modal-actions { display: flex; gap: 10px; }
        .sm-modal-del {
          flex: 1; padding: 12px;
          background: rgba(248,113,113,0.12); border: 1px solid rgba(248,113,113,0.3);
          border-radius: var(--radius-sm); color: var(--error-red);
          font-size: 14px; font-weight: 700; cursor: pointer;
          font-family: var(--font-body); transition: all 0.2s;
        }
        .sm-modal-del:hover { background: rgba(248,113,113,0.2); }
        .sm-modal-keep {
          flex: 1; padding: 12px;
          background: var(--surface2); border: 1px solid var(--border);
          border-radius: var(--radius-sm); color: var(--muted2);
          font-size: 14px; font-weight: 600; cursor: pointer;
          font-family: var(--font-body); transition: all 0.2s;
        }
        .sm-modal-keep:hover { color: var(--text); background: var(--surface3); }

        /* ── RESPONSIVE ── */
        @media (max-width: 900px) {
          .sm-field-row-3 { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 768px) {
          .sm-root { padding: 16px; }
          .sm-table-wrap-desk { display: none; }
          .sm-cards { display: flex; }
          .sm-field-row { grid-template-columns: 1fr; }
          .sm-field-row-3 { grid-template-columns: 1fr; }
          .sm-page-header { gap: 16px; }
          .sm-page-title { font-size: 22px; }
          .sm-steps { display: none; }
          .sm-form-panel { max-height: calc(100vh - 24px); }
          .sm-form-header { flex-direction: column; align-items: flex-start; gap: 12px; }
          .sm-btn-close { align-self: flex-end; }
          .sm-steps-bar { padding: 10px 16px; }
          .sm-form-body { padding: 16px; }
          .sm-form-footer { padding: 14px 16px; }
          .sm-form-footer-actions { width: 100%; justify-content: flex-end; }
          .sm-form-panel .sr-form-row,
          .sm-form-panel .sr-form-row--3 { grid-template-columns: 1fr; }
          .sm-form-panel .sr-segment-btn { min-height: 100px; padding: 16px 12px; }
          .sm-view-grid { grid-template-columns: 1fr; }
          .sm-pagination { flex-direction: column; align-items: stretch; gap: 12px; }
          .sm-pagination-controls { justify-content: center; }
        }
        @media (max-width: 480px) {
          .sm-toolbar { flex-direction: column; align-items: stretch; }
          .sm-search { max-width: none; }
          .sm-btn-add, .sm-btn-refresh { width: 100%; justify-content: center; }
          .sm-header-stats { display: none; }
          .sm-form-overlay { padding: 10px; }
          .sm-form-footer { flex-direction: column; align-items: stretch; }
          .sm-form-footer-actions { width: 100%; }
          .sm-form-footer-actions .sm-btn-cancel,
          .sm-form-footer-actions .sm-btn-save { width: 100%; justify-content: center; }
          .sm-view-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Toasts */}
      {successMessage && <div className="sm-toast sm-toast-success">✓ {successMessage}</div>}
      {error && <div className="sm-toast sm-toast-error">⚠ {error}</div>}

      {/* Actions Menu Modal */}
      {actionMenuStudent && (
        <div className="sm-modal-overlay" onClick={() => setActionMenuStudent(null)}>
          <div className="sm-actions-menu-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="sm-actions-title">
            <div className="sm-actions-menu-head">
              <div className="sm-actions-menu-head-main">
                <div className="sm-actions-menu-av" style={{ background: getAvatarColor(actionMenuStudent.fullName) }}>
                  {getInitials(actionMenuStudent.fullName)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="sm-actions-menu-eyebrow">Student actions</div>
                  <div className="sm-actions-menu-title" id="sm-actions-title">{actionMenuStudent.fullName}</div>
                  <div className="sm-actions-menu-sub">
                    {getStudentTypeLabel(actionMenuStudent.studentType)} · {getProgramLabel(actionMenuStudent)}
                  </div>
                </div>
              </div>
              <button type="button" className="sm-actions-menu-close" onClick={() => setActionMenuStudent(null)} aria-label="Close">✕</button>
            </div>
            <div className="sm-actions-menu-body">
              <div className="sm-actions-menu-label">Choose an action</div>
              <div className="sm-actions-menu-list">
                <button
                  type="button"
                  className="sm-actions-menu-item"
                  onClick={() => { setViewStudent(actionMenuStudent); setActionMenuStudent(null); }}
                >
                  <span className="sm-actions-menu-item-icon view" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" />
                    </svg>
                  </span>
                  <span className="sm-actions-menu-item-text">
                    <span className="sm-actions-menu-item-title">View student</span>
                    <span className="sm-actions-menu-item-desc">See full profile and contact details</span>
                  </span>
                  <span className="sm-actions-menu-item-chevron" aria-hidden="true">›</span>
                </button>
                <button type="button" className="sm-actions-menu-item" onClick={() => openEditFromMenu(actionMenuStudent)}>
                  <span className="sm-actions-menu-item-icon edit" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </span>
                  <span className="sm-actions-menu-item-text">
                    <span className="sm-actions-menu-item-title">Edit student</span>
                    <span className="sm-actions-menu-item-desc">Update registration information</span>
                  </span>
                  <span className="sm-actions-menu-item-chevron" aria-hidden="true">›</span>
                </button>
              </div>
              <div className="sm-actions-menu-divider" />
              <div className="sm-actions-menu-list">
                <button type="button" className="sm-actions-menu-item danger" onClick={() => openDeleteFromMenu(actionMenuStudent)}>
                  <span className="sm-actions-menu-item-icon delete" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </span>
                  <span className="sm-actions-menu-item-text">
                    <span className="sm-actions-menu-item-title">Delete student</span>
                    <span className="sm-actions-menu-item-desc">Permanently remove this record</span>
                  </span>
                  <span className="sm-actions-menu-item-chevron" aria-hidden="true">›</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Student Modal */}
      {viewStudent && (
        <div className="sm-modal-overlay" onClick={() => setViewStudent(null)}>
          <div className="sm-view-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="sm-view-title">
            <div className="sm-view-head">
              <div className="sm-view-head-left">
                <div className="sm-view-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div>
                  <div className="sm-view-title" id="sm-view-title">Student Profile</div>
                  <div className="sm-view-sub">Registration details and contact information</div>
                </div>
              </div>
              <button type="button" className="sm-actions-menu-close" onClick={() => setViewStudent(null)} aria-label="Close">✕</button>
            </div>
            <div className="sm-view-body">
              <div className="sm-view-student-card">
                <div className="sm-view-student-av" style={{ background: getAvatarColor(viewStudent.fullName) }}>
                  {getInitials(viewStudent.fullName)}
                </div>
                <div>
                  <div className="sm-view-student-name">{viewStudent.fullName}</div>
                  <div className="sm-view-student-meta">
                    {[viewStudent.email, viewStudent.phone].filter(Boolean).join(' · ') || 'No contact on file'}
                  </div>
                </div>
              </div>
              <div className="sm-view-grid">
                <div className="sm-view-field">
                  <div className="sm-view-field-label">Type</div>
                  <div className="sm-view-field-val">{getStudentTypeLabel(viewStudent.studentType)}</div>
                </div>
                <div className="sm-view-field">
                  <div className="sm-view-field-label">Program</div>
                  <div className="sm-view-field-val">{getProgramLabel(viewStudent)}</div>
                </div>
                <div className="sm-view-field">
                  <div className="sm-view-field-label">Batch</div>
                  <div className="sm-view-field-val">{getBatchLabel(viewStudent)}</div>
                </div>
                <div className="sm-view-field">
                  <div className="sm-view-field-label">Registered</div>
                  <div className="sm-view-field-val">{formatStudentDate(viewStudent.registrationDate || viewStudent.createdAt)}</div>
                </div>
                {viewStudent.studentType === STUDENT_TYPES.SECONDARY && (
                  <>
                    <div className="sm-view-field">
                      <div className="sm-view-field-label">School</div>
                      <div className="sm-view-field-val">{viewStudent.schoolName || '—'}</div>
                    </div>
                    <div className="sm-view-field">
                      <div className="sm-view-field-label">Level</div>
                      <div className="sm-view-field-val">{viewStudent.level || '—'}</div>
                    </div>
                  </>
                )}
                <div className="sm-view-field">
                  <div className="sm-view-field-label">Gender</div>
                  <div className="sm-view-field-val">{viewStudent.gender || '—'}</div>
                </div>
                <div className="sm-view-field">
                  <div className="sm-view-field-label">Date of birth</div>
                  <div className="sm-view-field-val">{formatStudentDate(viewStudent.dateOfBirth)}</div>
                </div>
                <div className="sm-view-field full">
                  <div className="sm-view-field-label">Address</div>
                  <div className="sm-view-field-val">{viewStudent.address || '—'}</div>
                </div>
                {(viewStudent.emergencyContact?.name || viewStudent.emergencyContact?.phone) && (
                  <div className="sm-view-field full">
                    <div className="sm-view-field-label">Emergency contact</div>
                    <div className="sm-view-field-val">
                      {[viewStudent.emergencyContact.name, viewStudent.emergencyContact.relation, viewStudent.emergencyContact.phone]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="sm-view-foot">
              <button type="button" className="sm-btn-cancel" onClick={() => setViewStudent(null)}>Close</button>
              <button
                type="button"
                className="sm-btn-view-edit"
                onClick={() => { const s = viewStudent; setViewStudent(null); handleEdit(s); }}
              >
                Edit student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="sm-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="sm-modal" onClick={e => e.stopPropagation()}>
            <div className="sm-modal-icon">🗑️</div>
            <div className="sm-modal-title">Delete Student?</div>
            <div className="sm-modal-sub">
              This will permanently remove{' '}
              <span className="sm-modal-name">{deleteConfirm.fullName}</span>{' '}
              and all associated records. This action cannot be undone.
            </div>
            <div className="sm-modal-actions">
              <button className="sm-modal-keep" onClick={() => setDeleteConfirm(null)}>Keep Student</button>
              <button className="sm-modal-del" onClick={() => handleDelete(deleteConfirm._id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="sm-root">
        {/* Page Header */}
        <div className="sm-page-header">
          <div>
            <div className="sm-page-eyebrow">✦ Admin Panel</div>
            <h1 className="sm-page-title">Student <span>Management</span></h1>
            <p className="sm-page-sub">View, add, edit and manage all enrolled students</p>
          </div>
          <div className="sm-header-stats">
            <div className="sm-mini-stat">
              <div className="sm-mini-stat-num">{students.length}</div>
              <div className="sm-mini-stat-label">Total</div>
            </div>
            <div className="sm-mini-stat">
              <div className="sm-mini-stat-num">{filteredStudents.length}</div>
              <div className="sm-mini-stat-label">{batchFilter ? 'In batch' : 'Shown'}</div>
            </div>
            {batchFilter && batchFilter !== '__none__' && selectedFilterBatch && getBatchCapacity(selectedFilterBatch) != null && (
              <div className="sm-mini-stat">
                <div className="sm-mini-stat-num">
                  {getBatchCapacity(selectedFilterBatch)}
                </div>
                <div className="sm-mini-stat-label">Capacity</div>
              </div>
            )}
          </div>
        </div>

        {/* Batch filter */}
        <div className="sm-batch-bar">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sm-batch-bar-label">Filter by batch</div>
            <select
              className="sm-input sm-batch-select"
              value={batchFilter}
              onChange={(e) => setBatchFilter(e.target.value)}
              aria-label="Batch filter"
            >
              <option value="">All batches ({students.length} students)</option>
              <option value="__none__">No batch assigned ({batchStudentCounts.__none__ || 0})</option>
              {sortedBatches.map((batch) => {
                const count = batchStudentCounts[batch._id] || 0;
                const trackLabel = batch.track === 'professional' ? 'Professional' : 'Secondary';
                const statusLabel = batch.status === 'running' ? ' · Running' : '';
                return (
                  <option key={batch._id} value={batch._id}>
                    {batch.name} · {trackLabel}{statusLabel} · {count} student{count === 1 ? '' : 's'}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {batchFilter && (
          <div className="sm-batch-summary">
            <div>
              <div className="sm-batch-summary-title">{selectedFilterBatchName}</div>
              <div className="sm-batch-summary-meta">
                {selectedFilterBatch && (
                  <>
                    {selectedFilterBatch.track === 'professional' ? 'Professional' : 'Secondary'}
                    {selectedFilterBatch.status === 'running' ? ' · Running' : ' · Closed'}
                    {formatBatchDates(selectedFilterBatch) ? ` · ${formatBatchDates(selectedFilterBatch)}` : ''}
                  </>
                )}
                {batchFilter === '__none__' && 'Students not linked to any cohort'}
                {(searchTerm || typeFilter) && ' · Count reflects search and type filters too'}
              </div>
            </div>
            <span className="sm-batch-summary-count">
              {filteredStudents.length} student{filteredStudents.length === 1 ? '' : 's'} in this batch
            </span>
            {selectedFilterBatch && getBatchCapacity(selectedFilterBatch) != null && (
              <span className="sm-batch-summary-meta">
                {filteredStudents.length} / {getBatchCapacity(selectedFilterBatch)} seats
              </span>
            )}
            <button type="button" className="sm-batch-summary-clear" onClick={() => setBatchFilter('')}>
              Clear batch filter
            </button>
          </div>
        )}

        {/* Toolbar */}
        <div className="sm-toolbar">
          <div className="sm-search">
            <span className="sm-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by name, email, field, school..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={{ background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:'16px',padding:'0 2px' }}>✕</button>
            )}
          </div>
          <select
            className="sm-input"
            style={{ width: 'auto', minWidth: 160 }}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All types</option>
            <option value={STUDENT_TYPES.SECONDARY}>Secondary</option>
            <option value={STUDENT_TYPES.PROFESSIONAL}>Professional</option>
          </select>
          <button className={`sm-btn-add${showForm ? ' cancel' : ''}`} onClick={() => { if (showForm) { resetForm(); } else { setShowForm(true); } }}>
            {showForm ? '✕  Cancel' : '+ Add Student'}
          </button>
          <button className="sm-btn-refresh" onClick={fetchStudents}>↻ Refresh</button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="sm-form-overlay" onClick={closeFormModal}>
            <div className="sm-form-panel" onClick={e => e.stopPropagation()}>
              <div className="sm-form-header">
              <div className="sm-form-title">
                <div className="sm-form-icon">{selectedStudent ? '✏️' : '👤'}</div>
                <div className="sm-form-title-wrap">
                  <span className="sm-form-eyebrow">Student record</span>
                  <span>{selectedStudent ? 'Edit Student' : 'Add New Student'}</span>
                  <span className="sm-form-subtitle">Choose the cohort, then fill required student details before saving. Admins can exceed batch capacity when needed.</span>
                </div>
              </div>
              <button
                type="button"
                className="sm-btn-close"
                onClick={closeFormModal}
              >
                ✕ Close
              </button>
              </div>
              <form onSubmit={selectedStudent ? handleUpdate : handleCreate}>
                <div className="sm-form-body">
                  <p className="sm-form-subtitle" style={{ marginBottom: 16 }}>
                    Assign the student to a batch below. Public registration is still blocked when a cohort is full — only admin can exceed seat capacity.
                    Required fields are marked with{' '}
                    <span style={{ color: 'var(--error-red)', fontWeight: 700 }}>*</span>.
                  </p>

                  <div className="sr-segment" role="tablist" aria-label="Student category">
                    {categoryOptions.map((opt) => {
                      const isActive = formData.studentType === opt.type;
                      return (
                        <motion.button
                          key={opt.type}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          className={`sr-segment-btn${isActive ? ' active' : ''}`}
                          onClick={() => handleSelectStudentType(opt.type)}
                          disabled={formSaving}
                          whileTap={formSaving ? {} : { scale: 0.99 }}
                        >
                          <div className="sr-segment-content">
                            <span className="sr-segment-icon" aria-hidden>{opt.icon}</span>
                            <span className="sr-segment-title">{opt.title}</span>
                            <span className="sr-segment-fee">{opt.fee}</span>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>

                  <SectionTitle label="Cohort assignment" />
                  <Field label="Batch / Cohort" required>
                    <select
                      className="sr-select"
                      name="batch"
                      value={formData.batch}
                      onChange={handleInputChange}
                      disabled={formSaving}
                      required
                    >
                      <option value="">Select a cohort</option>
                      {cohortOptions.map((b) => {
                        const stats = batchStatsMap[b._id] || { total: 0 };
                        const count = getBatchStudentCount(b, stats);
                        const capacity = getBatchCapacity(b);
                        const seatsLeft = getBatchSeatsLeft(b, stats);
                        const full = isBatchAtCapacity(b, stats);
                        const statusLabel = b.status === 'running' ? 'Running' : 'Closed';
                        let seatsLabel = 'No capacity limit';
                        if (capacity != null) {
                          seatsLabel = full
                            ? `FULL ${count}/${capacity}`
                            : `${seatsLeft} left (${count}/${capacity})`;
                        }
                        return (
                          <option key={b._id} value={b._id}>
                            {b.name} · {statusLabel} · {seatsLabel}
                          </option>
                        );
                      })}
                    </select>
                  </Field>
                  {cohortOptions.length === 0 && (
                    <p className="sm-cohort-hint">
                      No {trackForStudentType(formData.studentType)} cohorts found. Create a batch first in Batch Management.
                    </p>
                  )}
                  {selectedCohort && (
                    <div className={`sm-cohort-capacity${selectedCohortAtCapacity ? ' over' : ''}`}>
                      <div className="sm-cohort-capacity-title">
                        {selectedCohort.name}
                        {selectedCohort.status === 'running' ? ' · Open registration' : ' · Closed'}
                      </div>
                      <div className="sm-cohort-capacity-meta">
                        {formatBatchDates(selectedCohort) || 'Dates not set'}
                        {getBatchCapacity(selectedCohort) != null && (
                          <>
                            {' · '}
                            {getBatchStudentCount(selectedCohort, selectedCohortStats)}
                            /
                            {getBatchCapacity(selectedCohort)}
                            {' seats'}
                          </>
                        )}
                      </div>
                      {selectedCohortAtCapacity && (
                        <div className="sm-cohort-capacity-warn">
                          This cohort is at or over capacity. As admin you can still add this student
                          (seat limit is enforced on public registration only).
                        </div>
                      )}
                    </div>
                  )}

                  <SectionTitle label="Personal Information" />

                  <div className="sr-form-row">
                    <Field label="Full Name" required>
                      <input className="sr-input" type="text" name="fullName" value={formData.fullName}
                        onChange={handleInputChange} placeholder="Full name" required disabled={formSaving} />
                    </Field>
                    <Field label="Gender">
                      <select className="sr-select" name="gender" value={formData.gender}
                        onChange={handleInputChange} disabled={formSaving}>
                        <option value="">Select gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                      </select>
                    </Field>
                  </div>

                  <div className="sr-form-row">
                    <Field label="Date of Birth">
                      <input className="sr-input" type="date" name="dateOfBirth" value={formData.dateOfBirth}
                        onChange={handleInputChange} disabled={formSaving} />
                    </Field>
                    <Field label="Phone Number" required>
                      <input className="sr-input" type="tel" name="phone" value={formData.phone}
                        onChange={handleInputChange} placeholder="+250 7XX XXX XXX" required disabled={formSaving} />
                    </Field>
                  </div>

                  <div className="sr-form-row">
                    <Field label="Email Address" required>
                      <input className="sr-input" type="email" name="email" value={formData.email}
                        onChange={handleInputChange} placeholder="your@email.com" required disabled={formSaving} />
                    </Field>
                    <Field label="Address">
                      <input className="sr-input" type="text" name="address" value={formData.address}
                        onChange={handleInputChange} placeholder="District, Sector, Cell" disabled={formSaving} />
                    </Field>
                  </div>

                  <AnimatePresence mode="wait">
                    {formData.studentType === STUDENT_TYPES.SECONDARY && (
                      <motion.div
                        key="secondary-fields"
                        className="sr-category-fields"
                        variants={categorySectionMotion}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                      >
                        <SectionTitle label="Education" />
                        <div className="sr-form-row sr-form-row--3">
                          <Field label="Field of Study">
                            <select className="sr-select" name="fieldOfStudy" value={formData.fieldOfStudy}
                              onChange={handleInputChange} disabled={formSaving}>
                              <option value="">Trade / program</option>
                              {SECONDARY_TRADES.map((trade) => (
                                <option key={trade} value={trade}>{trade}</option>
                              ))}
                            </select>
                          </Field>
                          <Field label="School">
                            <input className="sr-input" type="text" name="schoolName" value={formData.schoolName}
                              onChange={handleInputChange} placeholder="School name" disabled={formSaving} />
                          </Field>
                          <Field label="Level">
                            <select className="sr-select" name="level" value={formData.level}
                              onChange={handleInputChange} disabled={formSaving}>
                              <option value="">Level</option>
                              <option value="Level 3">Level 3</option>
                              <option value="Level 4">Level 4</option>
                              <option value="Level 5">Level 5</option>
                              <option value="Other">Other</option>
                            </select>
                          </Field>
                        </div>
                        <details className="sr-details">
                          <summary>Emergency contact (optional)</summary>
                          <div className="sr-details-body">
                            <div className="sr-form-row">
                              <Field label="Contact Name">
                                <input className="sr-input" type="text" name="emergencyContact.name"
                                  value={formData.emergencyContact.name} onChange={handleInputChange}
                                  placeholder="Full name" disabled={formSaving} />
                              </Field>
                              <Field label="Contact Phone">
                                <input className="sr-input" type="tel" name="emergencyContact.phone"
                                  value={formData.emergencyContact.phone} onChange={handleInputChange}
                                  placeholder="+250 7XX XXX XXX" disabled={formSaving} />
                              </Field>
                            </div>
                            <Field label="Relationship">
                              <input className="sr-input" type="text" name="emergencyContact.relation"
                                value={formData.emergencyContact.relation} onChange={handleInputChange}
                                placeholder="e.g. Parent, Guardian" disabled={formSaving} />
                            </Field>
                          </div>
                        </details>
                      </motion.div>
                    )}

                    {formData.studentType === STUDENT_TYPES.PROFESSIONAL && (
                      <motion.div
                        key="professional-fields"
                        className="sr-category-fields"
                        variants={categorySectionMotion}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                      >
                        <SectionTitle label="Training Course" />
                        <Field label="Courses" required>
                          <div className="sr-course-options" role="group" aria-label="Professional courses">
                            {PROFESSIONAL_COURSES.map((course) => (
                              <label className="sr-course-option" key={course}>
                                <input
                                  type="checkbox"
                                  checked={formData.courses.includes(course)}
                                  onChange={() => handleCourseToggle(course)}
                                  disabled={formSaving}
                                />
                                <span>{course}</span>
                              </label>
                            ))}
                          </div>
                          <span className="sr-course-hint">Select one or more courses.</span>
                        </Field>
                        <details className="sr-details">
                          <summary>Work background (optional)</summary>
                          <div className="sr-details-body">
                            <div className="sr-form-row">
                              <Field label="Profession">
                                <input className="sr-input" type="text" name="profession" value={formData.profession}
                                  onChange={handleInputChange} placeholder="Job title" disabled={formSaving} />
                              </Field>
                              <Field label="Employer">
                                <input className="sr-input" type="text" name="employer" value={formData.employer}
                                  onChange={handleInputChange} placeholder="Organization" disabled={formSaving} />
                              </Field>
                            </div>
                            <Field label="Years of Experience">
                              <input className="sr-input" type="number" name="yearsOfExperience"
                                value={formData.yearsOfExperience} onChange={handleInputChange}
                                min="0" placeholder="0" disabled={formSaving} />
                            </Field>
                          </div>
                        </details>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="sm-form-footer">
                  <div className="sm-form-footer-actions">
                    <button type="button" className="sm-btn-cancel" onClick={closeFormModal} disabled={formSaving}>
                      Cancel
                    </button>
                    <button type="submit" className="sm-btn-save" disabled={formSaving}>
                      {formSaving ? 'Saving…' : 'Save Student'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="sm-table-card">
          <div className="sm-table-top">
            <div className="sm-table-title">
              Students
              <span className="sm-table-count">{filteredStudents.length}</span>
            </div>
            {(searchTerm || batchFilter || typeFilter) && (
              <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {batchFilter && (
                  <>
                    Batch: <span style={{ color: 'var(--accent)' }}>{selectedFilterBatchName}</span>
                    {' · '}
                  </>
                )}
                {searchTerm && (
                  <>
                    Search: "<span style={{ color: 'var(--accent)' }}>{searchTerm}</span>"
                  </>
                )}
              </span>
            )}
          </div>

          {loading ? (
            <div className="sm-loading"><div className="sm-dot"/><div className="sm-dot"/><div className="sm-dot"/></div>
          ) : filteredStudents.length === 0 ? (
            <div className="sm-empty">
              <span className="sm-empty-icon">👨‍🎓</span>
              <div className="sm-empty-title">
                {searchTerm || batchFilter || typeFilter ? 'No results found' : 'No students yet'}
              </div>
              <div className="sm-empty-sub">
                {searchTerm || batchFilter || typeFilter
                  ? 'Try a different search term or clear filters'
                  : 'Click "+ Add Student" to register the first student'}
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="sm-scroll sm-table-wrap-desk">
                <table className="sm-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Program</th>
                      <th>Phone</th>
                      <th>Registered</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedStudents.map((student, i) => (
                      <tr key={student._id} style={{ animationDelay: `${i * 40}ms` }}>
                        <td>
                          <div className="sm-student-cell">
                            <div className="sm-ava" style={{ background: getAvatarColor(student.fullName) }}>
                              {getInitials(student.fullName)}
                            </div>
                            <div>
                              <div className="sm-student-name">{student.fullName}</div>
                              <div className="sm-student-email">{student.email || '—'}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ color: 'var(--muted2)' }}>{getProgramLabel(student)}</td>
                        <td style={{ color: 'var(--muted2)' }}>{student.phone || '—'}</td>
                        <td style={{ color: 'var(--muted2)', fontSize: '12px' }}>
                          {student.registrationDate ? new Date(student.registrationDate).toLocaleDateString('en-GB', { day:'2-digit',month:'short',year:'numeric' }) : '—'}
                        </td>
                        <td>
                          <div className="sm-actions-cell">
                            <button
                              type="button"
                              className="sm-btn-more"
                              onClick={() => setActionMenuStudent(student)}
                              aria-label={`Actions for ${student.fullName}`}
                            >
                              ⋮
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="sm-cards" style={{ padding: '16px' }}>
                {paginatedStudents.map((student, i) => (
                  <div key={student._id} className="sm-card" style={{ animationDelay: `${i * 50}ms` }}>
                    <div className="sm-card-head">
                      <div className="sm-card-ava" style={{ background: getAvatarColor(student.fullName) }}>
                        {getInitials(student.fullName)}
                      </div>
                      <div>
                        <div className="sm-card-name">{student.fullName}</div>
                        <div className="sm-card-sub">{student.email || 'No email'}</div>
                      </div>
                      {student.level && <span className={`db-badge ${levelColor(student.level)}`} style={{ marginLeft: 'auto' }}>{student.level}</span>}
                    </div>
                    <div className="sm-card-grid">
                      <div className="sm-card-field"><dt>Program</dt><dd>{getProgramLabel(student)}</dd></div>
                      <div className="sm-card-field"><dt>Phone</dt><dd>{student.phone || '—'}</dd></div>
                      <div className="sm-card-field"><dt>Registered</dt><dd>{student.registrationDate ? new Date(student.registrationDate).toLocaleDateString() : '—'}</dd></div>
                    </div>
                    <div className="sm-card-actions">
                      <button type="button" className="sm-btn-edit" style={{ flex: 1 }} onClick={() => setActionMenuStudent(student)}>
                        ⋮ Actions
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="sm-pagination">
                <div className="sm-pagination-info">
                  Showing <strong>{rangeStart}–{rangeEnd}</strong> of <strong>{filteredStudents.length}</strong> students
                </div>
                <div className="sm-pagination-controls">
                  <select
                    className="sm-pagination-size"
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    aria-label="Rows per page"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n} / page</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="sm-pagination-btn"
                    disabled={safePage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    ← Prev
                  </button>
                  <span className="sm-pagination-page">Page {safePage} of {totalPages}</span>
                  <button
                    type="button"
                    className="sm-pagination-btn"
                    disabled={safePage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StudentManagement;